import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";

export interface ItemTypePreferences {
  activeItemTypes: Record<string, boolean>;
}

const STORAGE_KEY = "itemTypePreferences";
const DEFAULT_ACTIVE_STATE = true;

const loadPreferencesFromStorage = (): ItemTypePreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { activeItemTypes: {} };
  } catch {
    return { activeItemTypes: {} };
  }
};

const initialState: ItemTypePreferences = loadPreferencesFromStorage();

export const itemTypePreferencesSlice = createSlice({
  name: "itemTypePreferences",
  initialState,
  reducers: {
    toggleItemType: (state, action: PayloadAction<string>) => {
      const itemTypeId = action.payload;
      state.activeItemTypes[itemTypeId] = !state.activeItemTypes[itemTypeId];
    },
    setItemTypeActive: (
      state,
      action: PayloadAction<{ itemTypeId: string; active: boolean }>,
    ) => {
      const { itemTypeId, active } = action.payload;
      state.activeItemTypes[itemTypeId] = active;
    },
    setAllItemTypesActive: (state, action: PayloadAction<boolean>) => {
      const active = action.payload;
      // Get all item type IDs from the current state
      const itemTypeIds = Object.keys(state.activeItemTypes);
      itemTypeIds.forEach((id) => {
        state.activeItemTypes[id] = active;
      });
    },
    initializeItemTypes: (state, action: PayloadAction<string[]>) => {
      const itemTypeIds = action.payload;
      itemTypeIds.forEach((id) => {
        if (!(id in state.activeItemTypes)) {
          state.activeItemTypes[id] = DEFAULT_ACTIVE_STATE;
        }
      });
    },
  },
});

export const {
  toggleItemType,
  setItemTypeActive,
  setAllItemTypesActive,
  initializeItemTypes,
} = itemTypePreferencesSlice.actions;

export const selectActiveItemTypes = (state: RootState) =>
  state.itemTypePreferences.activeItemTypes;

export const selectIsItemTypeActive = (itemTypeId: string) => (state: RootState) =>
  state.itemTypePreferences.activeItemTypes[itemTypeId] ?? DEFAULT_ACTIVE_STATE;

export default itemTypePreferencesSlice.reducer;
